// src/constants/archetypes.ts
import { ArchetypeAssets, ArchetypeAssetMap } from "../assets/archetypeAssets";

export type ArchetypeId = "heartbeat" | "moments" | "safehaven";

export interface ArchetypeConfig {
  id: ArchetypeId;
  name: string;
  description: string;
  visualPersonality: string;
  loadingScreen: {
    style: "romantic" | "luxury" | "playful" | "minimal" | "cinematic" | "intimate" | "sacred";
    message: string;
    progressMessages: string[];
    icon: "heart" | "sparkle" | "flower" | "gift" | "candle" | "sunrise";
    backgroundColor: string;
    particleCount: number;
  };
  proposal?: {
    enabled: boolean;
    question: string;
    yesButtonText: string;
    noButtonTexts: string[];
    floatingHearts: boolean;
    confetti: boolean;
  };
  letter: {
    style:
      | "ceremonial"
      | "playful"
      | "heartfelt"
      | "reflective"
      | "elegant"
      | "romantic"
      | "minimal"
      | "sacred";
    paperTexture: string;
    seal: boolean;
  };
  bouquet: {
    receiveButtonText: string;
    showValue: boolean;
  };
  defaultCardData: {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    title?: string;
    message?: string;
    personalNote?: string;
    fontFamily?: string;
    includeHeartParticles?: boolean;
    includeSparkles?: boolean;
  } & ArchetypeAssetMap;
}

export const ARCHETYPES: Record<ArchetypeId, ArchetypeConfig> = {
  moments: {
    id: "moments",
    name: "Moments Experience",
    description:
      "Moments exists for recognition and celebration. Birthdays, milestones, achievements, invitations, gratitude, and the moments in life that deserve to be acknowledged and remembered.",
    visualPersonality:
      "Celebratory cinematic energy. Radiant light, warm gold tones, joyful motion and the feeling of being recognized and appreciated.",
    loadingScreen: {
      style: "luxury",
      message: "A meaningful moment is being prepared for you.",
      progressMessages: [
        "Preparing your moment...",
        "Bringing the details together...",
        "Adding a little celebration...",
        "This moment was created just for you.",
      ],
      icon: "sparkle",
      backgroundColor: "#f9ca24",
      particleCount: 30,
    },
    proposal: {
      enabled: true,
      question: "{name}, this moment was created for you.",
      yesButtonText: "Open My Moment",
      noButtonTexts: [
        "Give me a second",
        "Wait… really?",
        "I'm curious",
        "Okay I'm ready",
        "Let me see",
      ],
      floatingHearts: false,
      confetti: true,
    },
    letter: {
      style: "ceremonial",
      paperTexture: "texture",
      seal: true,
    },
    bouquet: {
      receiveButtonText: "Receive Your Gift",
      showValue: true,
    },
    defaultCardData: {
      primaryColor: "#f9ca24",
      secondaryColor: "#f6e58d",
      accentColor: "#f0932b",
      title: "A Moment Worth Celebrating",
      message:
        "This moment was created to recognize something meaningful in your life. A reminder that what you do and who you are matters.",
      personalNote: "You deserve to be celebrated.",
      fontFamily: "Inter",
      includeHeartParticles: false,
      includeSparkles: true,
      ...ArchetypeAssets.moments,
    },
  },

  heartbeat: {
    id: "heartbeat",
    name: "Heartbeat Connections",
    description:
      "Heartbeat exists for connection. Romance, friendship, appreciation, affection, apologies and the moments when someone wants to share what truly lives in their heart.",
    visualPersonality:
      "Warm emotional closeness. Soft light, gentle motion and the feeling of a sincere connection between people.",
    loadingScreen: {
      style: "romantic",
      message: "Someone took time to create this for you.",
      progressMessages: [
        "Opening something personal...",
        "Gathering the feelings behind it...",
        "Bringing the moment together...",
        "They hope this reaches your heart.",
      ],
      icon: "heart",
      backgroundColor: "#ff4d6d",
      particleCount: 20,
    },
    proposal: {
      enabled: true,
      question: "{name}, can I share something with you?",
      yesButtonText: "I'm Listening",
      noButtonTexts: ["Give me a second", "I'm curious", "Okay… show me", "Wait a moment"],
      floatingHearts: true,
      confetti: true,
    },
    letter: {
      style: "heartfelt",
      paperTexture: "texture",
      seal: true,
    },
    bouquet: {
      receiveButtonText: "Open My Surprise",
      showValue: true,
    },
    defaultCardData: {
      primaryColor: "#ff4d6d",
      secondaryColor: "#ff8fa3",
      accentColor: "#ffb3c6",
      title: "From The Heart",
      message:
        "Sometimes words are hard to say in everyday moments. This experience was created to share something genuine from the heart.",
      personalNote: "You matter more than you know.",
      fontFamily: "Inter",
      includeHeartParticles: true,
      includeSparkles: false,
      ...ArchetypeAssets.heartbeat,
    },
  },

  safehaven: {
    id: "safehaven",
    name: "Safe Haven",
    description:
      "Safe Haven exists for comfort and healing. Forgiveness, encouragement, prayer, recovery, support and the quiet moments when someone needs to feel understood and cared for.",
    visualPersonality:
      "Calm emotional safety. Soft light, quiet space and a sense of warmth, reflection and reassurance.",
    loadingScreen: {
      style: "minimal",
      message: "A gentle space has been prepared for you.",
      progressMessages: [
        "Creating a quiet moment...",
        "Letting the world slow down...",
        "Making space for what matters...",
        "You can take your time here.",
      ],
      icon: "flower",
      backgroundColor: "#a5b1c2",
      particleCount: 15,
    },

    proposal: {
      enabled: true,
      question: "{name}, someone prepared this space for you.",
      yesButtonText: "I'm Ready",
      noButtonTexts: [
        "Give me a moment",
        "Let me breathe first",
        "Not yet",
        "I'll come back",
        "Okay… I'm ready",
      ],
      floatingHearts: false,
      confetti: false,
    },

    letter: {
      style: "reflective",
      paperTexture: "texture",
      seal: true,
    },
    bouquet: {
      receiveButtonText: "Receive With Peace",
      showValue: false,
    },
    defaultCardData: {
      primaryColor: "#a5b1c2",
      secondaryColor: "#778ca3",
      accentColor: "#4b6584",
      title: "A Gentle Space",
      message:
        "This moment was created with care — a quiet space to breathe, reflect and receive support.",
      personalNote: "You are not alone.",
      fontFamily: "Inter",
      includeHeartParticles: false,
      includeSparkles: false,
      ...ArchetypeAssets.safehaven,
    },
  },
};

// Export individual archetypes for easier access
export const MOMENTS = ARCHETYPES.moments;
export const HEARTBEAT = ARCHETYPES.heartbeat;
export const SAFEHAVEN = ARCHETYPES.safehaven;
